// @flow
import React, { Component } from 'react';
import { Link } from 'react-router-dom';

import { withHelmet } from 'infrastructure/seo/Helmet';

import { dynamicClassName } from 'utils/dynamicClassName';
import { getPublicAssets } from 'utils/assetsUtils';
import { getDateFromString, formatFullDate } from 'utils/dateUtils';

import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import Sidebar from 'components/sidebar/Sidebar';
import CardBackground from 'components/fragments/card/CardBackground';
import Loading from 'components/fragments/Loading';
import FooterTeamArticle from 'components/team/FooterTeamArticle';
import Description from 'components/fragments/text/Description';
import ResultAndRanking from 'containers/team/teamDetail/ResultAndRankingContainer';

import type { CardType } from 'types/PageList';
import type { ArticleType } from 'types/Article';
import type { Status } from 'types/Status';
import type { BlocContent } from 'types/NewsletterBloc';
import type { TeamStatsType } from 'types/TeamStats';
import type { CompetitionResultType, ResultCalendarContainerType } from 'types/Result';
import type { RouterHistory } from 'react-router-dom';

import { MIN_EFFECTIF } from 'constants/TeamMenuConstants';

import { STATUS_LOADING } from 'constants/statusConstants';

export type StateProps = {
  description: BlocContent[],
  label: {
    id: number,
    name: string,
    slug: string
  },
  liste_cards: CardType[],
  articles: ArticleType[],
  status: Status,
  teamStats: TeamStatsType,
  teamStatsStatus: Status,
  structure: string,
  nextMatches: ResultCalendarContainerType,
  competitions: CompetitionResultType[],
  compte_joueurs: number,
  template: string,
  rejoignez_nous: string,
  rejoignez_nous_background: string,
  sidebar: BlocContent[]
};

type Props = {
  match: {
    params: {
      slug: string
    }
  },
  history: RouterHistory,
  pageSlug: string
} & StateProps;

class TeamDetail extends Component<Props> {
  renderBlocContent = () => {
    const { description } = this.props;
    return description.map((blocProps, index) => <ContentBloc key={index} {...blocProps} />);
  };

  renderCards = () => {
    const {
      match: {
        params: { slug }
      },
      liste_cards
    } = this.props;
    return liste_cards.map((card, index) => {
      const classNameContainer = dynamicClassName('col mb-2 mb-lg-4');
      index === 0 ? classNameContainer.add('col-md-8') : classNameContainer.add('col-md-4');
      if (index < 2) {
        return (
          <div key={index} className={classNameContainer.build()}>
            <CardBackground
              url={(index === 0 && `${slug}/joueurs`) || `${slug}/staff`}
              backgroundUrl={card.featured_media}
              title={card.title}
              desc={card.description}
              linkLabel={card.texte_cta}
            />
          </div>
        );
      } else {
        return null;
      }
    });
  };

  renderNews = () => {
    const { articles, label } = this.props;
    return (
      <div className="box box--white card card--news box--white">
        <div className="card__body">
          <h3 className="ft-h3 mb-2">Dernières actualités</h3>
          {articles &&
            articles.length > 0 &&
            articles.map((article, index) => {
              if (index < 4) {
                return (
                  <a className="news" key={index} href={article.link}>
                    <span className="news__title ft-h5">
                      {article.title}
                      <p className="ft-h6">{formatFullDate(getDateFromString(article.date))}</p>
                    </span>
                  </a>
                );
              }
              return null;
            })}
        </div>
        {label.slug && (
          <div className="card__footer">
            <Link
              to={`/actualites/${label.slug}`}
              title={`Voir plus`}
              className="link-chevron link-chevron--block"
              data-label=""
            >
              <span>{"Consulter plus d'actualités"}</span>
              <i className="icon icon-chevron-right"></i>
            </Link>
          </div>
        )}
      </div>
    );
  };

  renderStatsBoxItem = (facts: string[]): React$Element<'div'>[] => {
    const {
      teamStats: { teamFacts },
      compte_joueurs
    } = this.props;

    return facts
      .filter(fact => {
        return fact !== 'joueurs' || compte_joueurs;
      })
      .map(fact => {
        return (
          <div key={fact} className="player-stats__item">
            <p className="mb-1">{fact.replace('_', ' ')}</p>
            <span>{fact === 'joueurs' ? compte_joueurs : teamFacts[fact]}</span>
          </div>
        );
      });
  };

  renderSecondRow = () => {
    const {
      structure,
      teamStats: { teamFacts },
      match: {
        params: { slug }
      },
      compte_joueurs
    } = this.props;

    if (!teamFacts) {
      return null;
    }

    const teamFactsKeys = Object.keys(teamFacts);
    const clubSlug = structure.toLowerCase().replace(/ /g, '-');

    return (
      <div className="row mb-lg-1 home-competition">
        {!!compte_joueurs && compte_joueurs >= MIN_EFFECTIF && (
          <div className="col col-md-4 mb-2 mb-lg-4">
            {slug && (
              <Link
                to={`/equipes/${slug}/effectif`}
                title="Gérer mon club"
                className="box card card--hub card--hub--big card--hub-mobile-bcksp"
              >
                <div
                  className="card__thumbnail img-blue"
                  style={{ backgroundImage: `url(${getPublicAssets('/img/card/stadium.jpg')})` }}
                >
                  <img src={getPublicAssets('/img/card/stadium.jpg')} alt="Illustration effectif de l'équipe" />
                </div>
                <div className="card__body">
                  <h2 className="card__title">L&apos;effectif de l&apos;équipe</h2>
                  <p className="card__desc ft-secondary" />
                </div>
                <div className="card__footer">
                  <span className="btn btn--cheer">
                    <i className="icon icon-chevron-right link-icon is-dark  is-inline" />
                    <span>Voir l&apos;effectif</span>
                  </span>
                </div>
              </Link>
            )}
          </div>
        )}
        <div className="col col-md-4 mb-2 mb-lg-4">
          <div className="player-stats box player-stats--small">
            <h3 className="ft-h3">Les statistiques de l&apos;équipe</h3>
            {this.renderStatsBoxItem(teamFactsKeys)}
            <div className="player-stats__footer">
              <Link to={`/equipes/${slug}/statistiques`} className="link-chevron">
                <span>Voir toutes les statistiques de l&apos;équipe</span>
                <i className="icon icon-chevron-right" />
              </Link>
            </div>
          </div>
        </div>
        <div className="col col-md-4 mb-2 mb-lg-4">
          <a
            className="box card card--competition"
            href={`https://competitions.ffr.fr/clubs/${clubSlug}`}
            title="Accéder au site Compétitions FFR"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div
              className="card__thumbnail"
              style={{ backgroundImage: `url(${getPublicAssets('/img/card/bg-competition.jpg')})` }}
            />
            <div className="card__body">
              <img src={getPublicAssets('/img/logo/ffr_white_full.svg')} height="56" alt="logo FFR" />
              <h3 className="card__title">Compétitions</h3>
              <p className="card__desc ft-secondary">
                Abonnez vous aux résultats du <b>{structure}</b> et recevez en direct leurs résultats !
              </p>
            </div>
            <div className="card__footer">
              <span className="link-chevron link-chevron--white">
                Accéder au site
                <i className="icon icon-chevron-right" />
              </span>
            </div>
          </a>
        </div>
      </div>
    );
  };

  renderBanner = () => {
    const { rejoignez_nous, rejoignez_nous_background } = this.props;
    const background = rejoignez_nous_background ? rejoignez_nous_background : getPublicAssets('/img/card/bg-team.jpg');
    return (
      <div className="row">
        <div className="col">
          <a
            href={rejoignez_nous}
            title="Voir les modalités d'inscription"
            className="box card card--hub-alt card--hub-ffr card--small card--xSmall"
          >
            <div className="card__thumbnail" style={{ backgroundImage: `url(${background})` }}>
              <img src={background} alt="" />
            </div>
            <div className="card__body">
              <h3 className="card__title">Rejoignez-nous !</h3>
              <p className="card__desc ft-secondary">
                Nous sommes toujours à la recherche de nouveaux joueurs motivés.
              </p>
            </div>
            <div className="card__footer">
              <span className="btn btn--cheer">
                <i className="icon icon-chevron-right link-icon is-dark btn-cheer is-dark is-inline" />
                <span>Voir les modalités d&apos;inscription</span>
              </span>
            </div>
          </a>
        </div>
      </div>
    );
  };

  render() {
    const {
      description,
      label,
      status,
      articles,
      nextMatches,
      competitions,
      rejoignez_nous,
      template,
      sidebar
    } = this.props;

    if (status === STATUS_LOADING) {
      return <Loading />;
    }

    if (template !== 'equipe') {
      return (
        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <div className="col col-lg-8on9">
                <div className="page mt-2 mt-md-0">{this.renderBlocContent()}</div>
              </div>
            </div>
            <Sidebar sidebarContent={sidebar} />
          </div>
        </div>
      );
    }

    /**
     * Si pas de matchs à venir et plus de deux blocs disponibles,
     * on commence à compter à partir du second bloc présent,
     * le premier est théoriquement utilisé sur la home comme bandeau pour remplacer le lien
     * vers le prochain match
     */

    if (!nextMatches) {
      return <Loading />;
    }
    const isNextMatches = Object.keys(nextMatches).length > 0 || (competitions && competitions.length > 0);

    return (
      <>
        <div className="container section z2">
          {description && <Description description={description} />}
          {isNextMatches && <ResultAndRanking />}
          {isNextMatches && this.renderSecondRow()}
          {rejoignez_nous && this.renderBanner()}
        </div>
        {articles.length > 0 && (
          <FooterTeamArticle
            extraClasses={'container z1 half-card-hub'}
            articles={articles}
            slug={label.slug}
            label={label.name}
          />
        )}
      </>
    );
  }
}

export default withHelmet(TeamDetail)((Helmet, pageProps) => {
  return (
    <Helmet>
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
